import { pgTable, text, serial, integer, timestamp, real, jsonb, varchar, json, index, uniqueIndex, boolean } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

export const users = pgTable("users", {
  id: serial("id").primaryKey(),
  email: text("email").notNull().unique(),
  password: text("password").notNull(),
  name: text("name").notNull(),
});

export const projects = pgTable("projects", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull(),
  name: text("name").notNull(),
  type: text("type").notNull(),
  customer: text("customer"),
  address: text("address").notNull(),
  description: text("description"),
  status: text("status").notNull().default("draft"),
  currentStep: integer("current_step").notNull().default(1),
  createdAt: timestamp("created_at").defaultNow(),
  objektbeschaffenheit: text("objektbeschaffenheit").array(),
  massnahmen: text("massnahmen").array(),
  geschosszahl: integer("geschosszahl"),
  hoehe: real("hoehe"),
  menge: real("menge"),
  mengeneinheit: text("mengeneinheit"),
  // Server-Persistenz für LV-Kalkulator-State (Mengen, Refs, AutoFill).
  // Enthält keine fachlichen Foto-Daten (Beschreibungen, Positionen) — die liegen weiterhin auf `photos`.
  lvState: jsonb("lv_state"),
  // Server-Persistenz für Angebots-Daten (Notizen, Gültigkeit, Summen-Snapshot).
  quoteData: jsonb("quote_data"),
  // Server-Persistenz für Leistungsprotokoll-Daten (Text, Datum, ausgewählte Fotos).
  protokollData: jsonb("protokoll_data"),
});

export const photos = pgTable("photos", {
  id: serial("id").primaryKey(),
  projectId: integer("project_id").notNull(),
  fileName: text("file_name").notNull(),
  // Frei editierbarer Anzeigename; der technische Originaldateiname bleibt erhalten.
  displayName: text("display_name"),
  // Manuell festgelegte Reihenfolge in Schadenserfassung, Angebot und PDF.
  sortOrder: integer("sort_order"),
  mimeType: text("mime_type").notNull(),
  fileSize: integer("file_size").notNull(),
  data: text("data").notNull(),
  annotatedData: text("annotated_data"),
  description: text("description"),
  photoPositionen: jsonb("photo_positionen"),
  measurementData: jsonb("measurement_data"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const invoices = pgTable("invoices", {
  id: serial("id").primaryKey(),
  projectId: integer("project_id").notNull().references(() => projects.id, { onDelete: "cascade" }),
  userId: integer("user_id").notNull(),
  invoiceNumber: text("invoice_number"),
  data: jsonb("data").$type<import("./invoice").InvoiceData>().notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [uniqueIndex("invoices_user_number_unique").on(table.userId, table.invoiceNumber)]);

export const invoicePhotos = pgTable("invoice_photos", {
  id: serial("id").primaryKey(),
  invoiceId: integer("invoice_id").notNull().references(() => invoices.id, { onDelete: "cascade" }),
  fileName: text("file_name").notNull(),
  displayName: text("display_name").notNull().default(""),
  description: text("description").notNull().default(""),
  mimeType: text("mime_type").notNull(),
  fileSize: integer("file_size").notNull(),
  data: text("data").notNull(),
});

// Externe Tabelle, verwaltet von connect-pg-simple. Hier nur deklariert,
// damit drizzle-kit sie nicht als „zu löschen" interpretiert.
export const sessionTable = pgTable(
  "session",
  {
    sid: varchar("sid").primaryKey(),
    sess: json("sess").notNull(),
    expire: timestamp("expire", { precision: 6 }).notNull(),
  },
  (t) => ({
    expireIdx: index("IDX_session_expire").on(t.expire),
  }),
);

export const knowledgeEntries = pgTable("knowledge_entries", {
  id: serial("id").primaryKey(),
  category: text("category").notNull(),
  title: text("title").notNull(),
  description: text("description").notNull(),
  materials: text("materials").array().notNull().default([]),
  damageTypes: text("damage_types").array().notNull().default([]),
  repairMethods: text("repair_methods").array().notNull().default([]),
  recommendations: text("recommendations").array().notNull().default([]),
  priority: text("priority").notNull().default("medium"),
  costMin: integer("cost_min").notNull().default(0),
  costMax: integer("cost_max").notNull().default(0),
  costUnit: text("cost_unit").notNull().default("EUR/m²"),
});

export const suggestedPositionSchema = z.object({
  kurztext: z.string().min(1).max(300),
  einheit: z.string().min(1).max(20),
  menge: z.number().nonnegative().default(1),
  einzelpreisVon: z.number().nonnegative().default(0),
  einzelpreisBis: z.number().nonnegative().default(0),
  begruendung: z.string().max(500).optional().default(""),
});
export type SuggestedPosition = z.infer<typeof suggestedPositionSchema>;

/**
 * Ein Bild, das mit einer Analyse persistiert wird, damit der Verlauf nicht
 * nur die Dateinamen, sondern auch die tatsächlichen Bilder zeigt. `data`
 * enthält Base64 (ohne data-URI-Präfix) — wird vom Server in
 * `GET /api/photo-analyses/:id/images/:idx` als Binary ausgeliefert.
 * Im Listen-Endpunkt (`GET /api/photo-analyses`) wird `data` aus
 * Performance-Gründen serverseitig auf einen leeren String gestrippt; das
 * Frontend nutzt dort nur `fileName`/`mimeType` und lädt das Binary über
 * den dedizierten Image-Endpunkt.
 */
export const photoAnalysisImageSchema = z.object({
  fileName: z.string().min(1).max(255),
  mimeType: z.string().min(1).max(100),
  data: z.string().default(""),
});
export type PhotoAnalysisImage = z.infer<typeof photoAnalysisImageSchema>;

export const photoAnalyses = pgTable("photo_analyses", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull(),
  reportType: text("report_type").notNull(),
  context: text("context"),
  imageNames: text("image_names").array().notNull().default([]),
  /** Persistierte Bilder zur Analyse — siehe photoAnalysisImageSchema. */
  images: jsonb("images").$type<PhotoAnalysisImage[]>().notNull().default([]),
  generatedText: text("generated_text").notNull().default(""),
  suggestedPositions: jsonb("suggested_positions").$type<SuggestedPosition[]>().notNull().default([]),
  createdAt: timestamp("created_at").defaultNow(),
});

export const photoAnalysisReportTypes = ["schadensbericht", "leistungsbeschreibung"] as const;
export type PhotoAnalysisReportType = typeof photoAnalysisReportTypes[number];

export const insertPhotoAnalysisSchema = createInsertSchema(photoAnalyses).omit({
  id: true,
  userId: true,
  createdAt: true,
}).extend({
  reportType: z.enum(photoAnalysisReportTypes),
  context: z.string().nullable().optional(),
  imageNames: z.array(z.string()).default([]),
  images: z.array(photoAnalysisImageSchema).default([]),
  generatedText: z.string().default(""),
});

export type InsertPhotoAnalysis = z.infer<typeof insertPhotoAnalysisSchema>;
export type PhotoAnalysis = typeof photoAnalyses.$inferSelect;

export const analyzePhotosRequestSchema = z.object({
  reportType: z.enum(photoAnalysisReportTypes),
  context: z.string().max(2000).optional().default(""),
  images: z.array(z.object({
    fileName: z.string().min(1),
    mimeType: z.string().regex(/^image\/(png|jpeg|jpg|webp|gif)$/i, "Nur PNG/JPEG/WEBP/GIF unterstützt"),
    data: z.string().min(1),
  })).min(1, "Mindestens ein Foto erforderlich").max(8, "Maximal 8 Fotos pro Analyse"),
});
export type AnalyzePhotosRequest = z.infer<typeof analyzePhotosRequestSchema>;

export const updatePhotoAnalysisSchema = z.object({
  generatedText: z.string(),
  suggestedPositions: z.array(suggestedPositionSchema).optional(),
});
export type UpdatePhotoAnalysisData = z.infer<typeof updatePhotoAnalysisSchema>;

// Einzelner Preis-Sample mit Mengen-Kontext (für "großer Auftrag = niedrigerer EP",
// "kleiner Auftrag = höherer EP" Logik).
export const priceSampleSchema = z.object({
  menge: z.number().nonnegative(),
  ep: z.number().nonnegative(),
  datum: z.string(),
  quelle: z.string().max(50),
  projektId: z.number().nullable().optional(),
  analysisId: z.number().nullable().optional(),
  note: z.string().max(300).nullable().optional(),
});
export type PriceSample = z.infer<typeof priceSampleSchema>;

export const libraryPositionStatusValues = ["entwurf", "freigegeben"] as const;
export type LibraryPositionStatus = typeof libraryPositionStatusValues[number];

export const libraryPositionQuelleValues = [
  "manuell",
  "ki-foto",
  "ki-pdf",
  "ki-excel",
  "ki-screenshot",
  "import",
  "projekt",
] as const;
export type LibraryPositionQuelle = typeof libraryPositionQuelleValues[number];

export const libraryPositions = pgTable("library_positions", {
  id: serial("id").primaryKey(),
  // userId: nullable für bestehende (globale) Einträge aus der Vor-Migration.
  // Neu angelegte Einträge haben immer einen userId.
  userId: integer("user_id"),
  sektionId: text("sektion_id").notNull(),
  name: text("name").notNull(),
  unit: text("unit").notNull(),
  ep: real("ep").notNull().default(0),
  // Preis-Spanne (netto, EUR) — optional; wenn beide > 0 gesetzt sind, wird
  // das beim LV-Lookup für eine Spannen-Anzeige verwendet.
  epVon: real("ep_von"),
  epBis: real("ep_bis"),
  tag: text("tag"),
  aktiv: boolean("aktiv").notNull().default(true),
  // Neue Workflow-Felder:
  status: text("status").notNull().default("freigegeben"),
  quelle: text("quelle").notNull().default("manuell"),
  beschreibung: text("beschreibung"),
  region: text("region"),
  // Historische Preis-Stichproben (menge, ep, datum, quelle, optionale IDs).
  priceSamples: jsonb("price_samples").$type<PriceSample[]>().notNull().default([]),
  zuletztVerwendet: timestamp("zuletzt_verwendet"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertLibraryPositionSchema = createInsertSchema(libraryPositions).omit({
  id: true,
  userId: true,
  createdAt: true,
  zuletztVerwendet: true,
}).extend({
  sektionId: z.enum(["s1", "s2", "s3", "s4", "s5", "s6", "s7", "s8"]),
  name: z.string().min(1, "Name erforderlich"),
  unit: z.string().min(1, "Einheit erforderlich"),
  // Deductions in K7 templates are genuine signed unit prices.
  ep: z.number().finite(),
  epVon: z.number().nonnegative().nullable().optional(),
  epBis: z.number().nonnegative().nullable().optional(),
  tag: z.string().nullable().optional(),
  aktiv: z.boolean().optional(),
  status: z.enum(libraryPositionStatusValues).optional(),
  quelle: z.enum(libraryPositionQuelleValues).optional(),
  beschreibung: z.string().max(2000).nullable().optional(),
  region: z.string().max(10).nullable().optional(),
  priceSamples: z.array(priceSampleSchema).optional(),
});

export const bulkInsertLibraryPositionsSchema = z.object({
  items: z.array(insertLibraryPositionSchema).min(1),
});

export const addPriceSampleSchema = priceSampleSchema.omit({ datum: true }).extend({
  datum: z.string().optional(),
});

export const extractLibraryPositionsSchema = z.object({
  kind: z.enum(["pdf", "excel", "image", "csv"]),
  fileName: z.string().min(1),
  mimeType: z.string().min(1),
  data: z.string().min(1),
  region: z.string().max(10).nullable().optional(),
});
export type ExtractLibraryPositionsRequest = z.infer<typeof extractLibraryPositionsSchema>;

export type InsertLibraryPosition = z.infer<typeof insertLibraryPositionSchema>;
export type LibraryPosition = typeof libraryPositions.$inferSelect;
export type AddPriceSampleData = z.infer<typeof addPriceSampleSchema>;

export const insertUserSchema = createInsertSchema(users).omit({
  id: true,
});

export const insertProjectSchema = createInsertSchema(projects).omit({
  id: true,
  userId: true,
  createdAt: true,
}).extend({
  geschosszahl: z.preprocess(
    (v) => (typeof v === "number" && isNaN(v) ? null : v),
    z.number().int().nullable().optional()
  ),
  hoehe: z.preprocess(
    (v) => (typeof v === "number" && isNaN(v) ? null : v),
    z.number().nullable().optional()
  ),
  menge: z.preprocess(
    (v) => (typeof v === "number" && isNaN(v) ? null : v),
    z.number().nullable().optional()
  ),
});

export const insertKnowledgeEntrySchema = createInsertSchema(knowledgeEntries).omit({
  id: true,
});

export const insertPhotoSchema = createInsertSchema(photos).omit({
  id: true,
  createdAt: true,
});

export const uploadPhotoSchema = z.object({
  fileName: z.string().min(1),
  mimeType: z.string().min(1),
  fileSize: z.number().int().nonnegative(),
  data: z.string().min(1),
});

export const updatePhotoAnnotationSchema = z.object({
  annotatedData: z.string().nullable(),
});

export const updatePhotoMetadataSchema = z.object({
  displayName: z.string().trim().max(255).nullable().optional(),
  sortOrder: z.number().int().nonnegative().nullable().optional(),
  description: z.string().nullable().optional(),
  photoPositionen: z.any().nullable().optional(),
  measurementData: z.any().nullable().optional(),
});

export const loginSchema = z.object({
  email: z.string().email("Ungültige E-Mail-Adresse"),
  password: z.string().min(1, "Passwort ist erforderlich"),
});

export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1, "Aktuelles Passwort ist erforderlich"),
  newPassword: z.string().min(8, "Neues Passwort muss mindestens 8 Zeichen lang sein"),
  confirmPassword: z.string().min(1, "Passwort-Bestätigung ist erforderlich"),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Passwörter stimmen nicht überein",
  path: ["confirmPassword"],
});

export type InsertUser = z.infer<typeof insertUserSchema>;
export type User = typeof users.$inferSelect;
export type InsertProject = z.infer<typeof insertProjectSchema>;
export type Project = typeof projects.$inferSelect;
export type InsertKnowledgeEntry = z.infer<typeof insertKnowledgeEntrySchema>;
export type KnowledgeEntry = typeof knowledgeEntries.$inferSelect;
export type LoginData = z.infer<typeof loginSchema>;
export type ChangePasswordData = z.infer<typeof changePasswordSchema>;
export type InsertPhoto = z.infer<typeof insertPhotoSchema>;
export type Photo = typeof photos.$inferSelect;
export type PhotoMetadata = Omit<Photo, "data" | "annotatedData"> & {
  hasAnnotation: boolean;
};
export type UploadPhotoData = z.infer<typeof uploadPhotoSchema>;
export type UpdatePhotoMetadataData = z.infer<typeof updatePhotoMetadataSchema>;

export const exportPhotoSchema = z.object({
  id: z.number(),
  fileName: z.string(),
  displayName: z.string().nullable().optional(),
  sortOrder: z.number().int().nonnegative().nullable().optional(),
  mimeType: z.string(),
  fileSize: z.number(),
  data: z.string(),
  annotatedData: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  photoPositionen: z.any().nullable().optional(),
  measurementData: z.any().nullable().optional(),
});
export type ExportPhoto = z.infer<typeof exportPhotoSchema>;

export const exportPhotoAnalysisSchema = z.object({
  reportType: z.string(),
  context: z.string().nullable().optional(),
  imageNames: z.array(z.string()).default([]),
  images: z.array(photoAnalysisImageSchema).default([]),
  generatedText: z.string().default(""),
  suggestedPositions: z.array(z.any()).default([]),
});
export type ExportPhotoAnalysis = z.infer<typeof exportPhotoAnalysisSchema>;

export const projectExportSchema = z.object({
  schemaVersion: z.literal(1),
  exportedAt: z.string(),
  project: z.object({
    name: z.string(),
    type: z.string(),
    customer: z.string().nullable().optional(),
    address: z.string(),
    description: z.string().nullable().optional(),
    status: z.string(),
    currentStep: z.number(),
    objektbeschaffenheit: z.array(z.string()).nullable().optional(),
    massnahmen: z.array(z.string()).nullable().optional(),
    geschosszahl: z.number().nullable().optional(),
    hoehe: z.number().nullable().optional(),
    menge: z.number().nullable().optional(),
    mengeneinheit: z.string().nullable().optional(),
    lvState: z.any().nullable().optional(),
    quoteData: z.any().nullable().optional(),
  }),
  photos: z.array(exportPhotoSchema),
  photoAnalyses: z.array(exportPhotoAnalysisSchema).default([]),
});
export type ProjectExport = z.infer<typeof projectExportSchema>;

export const appSettings = pgTable("app_settings", {
  key: text("key").primaryKey(),
  value: text("value").notNull(),
});

export const saveSettingsSchema = z.object({
  anthropicApiKey: z.string().min(1).optional(),
  openaiApiKey: z.string().min(1).optional(),
});
export type SaveSettingsData = z.infer<typeof saveSettingsSchema>;
